/**
 * AdminUserEditor — RESTRICTED TO JOHAN (johan@beaversden.co.za) ONLY
 * Inline editor for user personal info + KYC data.
 * Used inside ConsolidatedCertificateAdmin user detail modal.
 * v4.3.45 — Fixed DOC_TYPES to match DB CHECK constraint values
 * Birthdate shows day/month/year (12/10/1992) with a calendar: day, then month, then year.
 * The saved value stays YYYY-MM-DD.
 */
import React, { useState, useEffect } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';
import { dayMonthYearToIsoDate, isoDateToDayMonthYear } from '../../lib/kycDateDisplay.js';
import { KycBirthdateField } from './KycBirthdateField';

const AUTHORIZED_EMAIL = 'johan@beaversden.co.za';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface AdminUserEditorProps {
  userId: number;
  adminEmail: string; // Current admin's email
  onSaved?: () => void; // Callback after successful save
}

interface UserData {
  full_name: string;
  phone: string;
  phone_number: string;
  country_of_residence: string;
  country_name: string;
}

interface KycData {
  first_name: string;
  last_name: string;
  full_legal_name: string;
  id_type: string;
  id_number_decrypted: string;
  phone_number: string;
  email_address: string;
  street_address: string;
  city: string;
  province: string;
  postal_code: string;
  country_code: string;
  country_name: string;
  nationality: string;
  date_of_birth: string;
  admin_notes: string;
  document_verification_override: boolean;
}

const emptyKyc: KycData = {
  first_name: '', last_name: '', full_legal_name: '', id_type: 'national_id',
  id_number_decrypted: '', phone_number: '', email_address: '', street_address: '',
  city: '', province: '', postal_code: '', country_code: '', country_name: '',
  nationality: '', date_of_birth: '', admin_notes: '', document_verification_override: false
};

interface KycDocument {
  id: string;
  document_type: string;
  file_name: string;
  url: string;
  verification_status: string;
  uploaded_at: string;
  file_size: number;
  admin_notes: string;
}

// Document types must match the DB CHECK constraint on kyc_documents.document_type:
// ('id_document', 'proof_of_address', 'selfie', 'additional', 'company_registration')
const DOC_TYPES = [
  { key: 'id_document', label: 'ID Document', icon: '🪪' },
  { key: 'selfie', label: 'Selfie / Photo', icon: '🤳' },
  { key: 'proof_of_address', label: 'Proof of Address', icon: '🏠' },
  { key: 'additional', label: 'Additional Document', icon: '📄' },
];

export const AdminUserEditor: React.FC<AdminUserEditorProps> = ({ userId, adminEmail, onSaved }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [kycData, setKycData] = useState<KycData>(emptyKyc);
  const [hasKyc, setHasKyc] = useState(false);
  const [documents, setDocuments] = useState<KycDocument[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);
  // BSC Wallet removal state
  const [bscWallet, setBscWallet] = useState<{ id: string; wallet_address: string; registered_at: string; registered_by: string } | null>(null);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletNote, setWalletNote] = useState('');
  const [walletRemoving, setWalletRemoving] = useState(false);
  const [walletConfirm, setWalletConfirm] = useState(false);
  // Email change state
  const [userEmail, setUserEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailReason, setEmailReason] = useState('');
  const [emailChanging, setEmailChanging] = useState(false);
  const [emailConfirm, setEmailConfirm] = useState(false);
  const [dobText, setDobText] = useState('');
  const [dobError, setDobError] = useState<string | null>(null);

  useEffect(() => {
    setDobText(isoDateToDayMonthYear(kycData.date_of_birth));
  }, [kycData.date_of_birth]);

  // Only Johan can see/use this component
  if (adminEmail?.toLowerCase() !== AUTHORIZED_EMAIL.toLowerCase()) return null;

  const callAPI = async (action: string, updates?: any) => {
    const res = await fetch(`${API_BASE_URL}/api/admin/edit-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({ action, targetUserId: userId, updates })
    });
    return res.json();
  };

  const loadDocuments = async () => {
    try {
      const result = await callAPI('get_documents');
      if (result.success) setDocuments(result.documents || []);
    } catch { /* ignore */ }
  };

  const loadBscWallet = async () => {
    setWalletLoading(true);
    try {
      const result = await callAPI('get_bsc_wallet');
      if (result.success) setBscWallet(result.wallet || null);
    } catch { /* ignore */ }
    finally { setWalletLoading(false); }
  };

  const removeBscWallet = async () => {
    if (!walletNote.trim() || walletNote.trim().length < 5) {
      setError('Please provide a reason (at least 5 characters) for removing the wallet.');
      return;
    }
    setWalletRemoving(true); setError(null); setSuccess(null);
    try {
      const result = await callAPI('remove_bsc_wallet', { reason: walletNote.trim() });
      if (!result.success) throw new Error(result.error);
      setSuccess(`BSC wallet removed: ${result.removed_wallet}`);
      setBscWallet(null);
      setWalletNote('');
      setWalletConfirm(false);
      onSaved?.();
    } catch (err: any) { setError(err.message); }
    finally { setWalletRemoving(false); }
  };

  const handleFileUpload = async (documentType: string, file: File) => {
    if (!file) return;
    // Validate file type
    const validMimes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validMimes.includes(file.type)) {
      setError('Invalid file type. Use JPEG, PNG, WebP, or PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum 10MB.');
      return;
    }

    setUploading(documentType);
    setError(null);
    setSuccess(null);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const base64 = btoa(new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), ''));
      const fileBase64 = `data:${file.type};base64,${base64}`;

      const result = await callAPI('upload_document', {
        fileBase64, fileName: file.name, fileType: file.type, fileSize: file.size, documentType
      });
      if (!result.success) throw new Error(result.error);
      setSuccess(`${documentType} uploaded successfully${result.replaced ? ' (replaced existing)' : ''}`);
      await loadDocuments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(null);
    }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [result] = await Promise.all([callAPI('get_user_details'), loadDocuments(), loadBscWallet()]);
      if (result.success) {
        setUserData(result.user);
        setUserEmail(result.user?.email || '');
        if (result.kyc) {
          setKycData({ ...emptyKyc, ...result.kyc, id_number_decrypted: result.kyc.id_number_decrypted || '' });
          setHasKyc(true);
        } else {
          setKycData(emptyKyc);
          setHasKyc(false);
        }
      } else {
        setError(result.error || 'Failed to load user data');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!expanded) loadData();
    setExpanded(!expanded);
  };

  const saveUser = async () => {
    if (!userData) return;
    setSaving(true); setError(null); setSuccess(null);
    try {
      const result = await callAPI('update_user', userData);
      if (!result.success) throw new Error(result.error);
      setSuccess('User info updated');
      onSaved?.();
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  };

  const saveKyc = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const updates: any = { ...kycData };
      const trimmedDob = dobText.trim();
      if (trimmedDob) {
        const isoDob = dayMonthYearToIsoDate(trimmedDob);
        if (!isoDob) {
          setDobError('Use day/month/year, for example 12/10/1992');
          setError('Date of birth must be day/month/year, for example 12/10/1992.');
          setSaving(false);
          return;
        }
        updates.date_of_birth = isoDob;
      }
      if (kycData.id_number_decrypted) updates.id_number = kycData.id_number_decrypted;
      delete updates.id_number_decrypted;
      // full_legal_name is a DB GENERATED ALWAYS column (first_name + last_name);
      // sending it causes Postgres 428C9 and fails the whole KYC save.
      delete updates.full_legal_name;
      // Remove empty optional fields
      Object.keys(updates).forEach(k => { if (updates[k] === '' || updates[k] === null) delete updates[k]; });
      const result = await callAPI('update_kyc', updates);
      if (!result.success) throw new Error(result.error);
      setSuccess(result.created ? 'KYC record created' : 'KYC updated');
      setHasKyc(true);
      onSaved?.();
    } catch (err: any) { setError(err.message); }
    finally { setSaving(false); }
  };

  const Field = ({ label, value, onChange, type = 'text', placeholder = '' }: any) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-400">{label}</label>
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="bg-gray-700 text-white text-sm px-3 py-1.5 rounded border border-gray-600 focus:border-blue-500 focus:outline-none" />
    </div>
  );

  return (
    <div className="mb-4">
      <button onClick={handleToggle}
        className="w-full px-4 py-2 bg-blue-700 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors text-sm">
        {expanded ? '▼' : '▶'} ✏️ Edit User Info & KYC (Johan Only)
      </button>

      {expanded && (
        <div className="mt-3 bg-gray-800 rounded-lg border border-blue-500/30 p-4">
          {loading && <p className="text-gray-400 text-sm">Loading...</p>}
          {error && <p className="text-red-400 text-sm mb-2">❌ {error}</p>}
          {success && <p className="text-green-400 text-sm mb-2">✅ {success}</p>}

          {userData && (
            <>
              {/* User Profile Section */}
              <h5 className="text-sm font-bold text-blue-400 mb-2">👤 User Profile (users table)</h5>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Field label="Full Name" value={userData.full_name} onChange={(v: string) => setUserData({...userData, full_name: v})} />
                <Field label="Phone" value={userData.phone || userData.phone_number} onChange={(v: string) => setUserData({...userData, phone: v, phone_number: v})} />
                <Field label="Country" value={userData.country_of_residence || userData.country_name} onChange={(v: string) => setUserData({...userData, country_of_residence: v, country_name: v})} />
              </div>
              <button onClick={saveUser} disabled={saving}
                className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-500 disabled:bg-gray-600 mb-4">
                {saving ? 'Saving...' : '💾 Save User Profile'}
              </button>

              {/* BSC Wallet Management — Johan Only */}
              <h5 className="text-sm font-bold text-yellow-400 mt-4 mb-2">🔗 BSC Wallet</h5>
              <div className="mb-4 p-3 rounded-lg border border-yellow-600/40 bg-yellow-900/10">
                {walletLoading ? (
                  <p className="text-gray-400 text-sm">Loading wallet...</p>
                ) : bscWallet ? (
                  <div>
                    <div className="bg-gray-700/50 rounded p-3 mb-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-yellow-400 font-medium text-sm">BSC (BNB Smart Chain)</span>
                          <p className="text-gray-300 font-mono text-xs mt-1 break-all">{bscWallet.wallet_address}</p>
                          <p className="text-gray-500 text-xs mt-1">
                            Registered {new Date(bscWallet.registered_at).toLocaleDateString()} by {bscWallet.registered_by}
                          </p>
                        </div>
                        <span className="text-green-400 text-xs">🔒 Active</span>
                      </div>
                    </div>
                    {!walletConfirm ? (
                      <button onClick={() => setWalletConfirm(true)}
                        className="px-3 py-1.5 bg-red-700 text-white text-sm rounded hover:bg-red-600 transition-colors">
                        🗑️ Remove BSC Wallet
                      </button>
                    ) : (
                      <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-3">
                        <p className="text-red-300 text-xs font-bold mb-2">⚠️ CONFIRM WALLET REMOVAL</p>
                        <p className="text-gray-300 text-xs mb-2">
                          This will remove the locked BSC wallet for this user. They will need to register a new wallet before they can withdraw.
                        </p>
                        <label className="text-xs text-gray-400 block mb-1">Reason for removal (required) *</label>
                        <textarea value={walletNote} onChange={e => setWalletNote(e.target.value)}
                          placeholder="e.g. Client requested wallet change due to compromised wallet / new device..."
                          className="w-full bg-gray-700 text-white text-sm px-3 py-1.5 rounded border border-red-600/50 focus:border-red-400 focus:outline-none mb-2" rows={2} />
                        <div className="flex gap-2">
                          <button onClick={removeBscWallet} disabled={walletRemoving || walletNote.trim().length < 5}
                            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed">
                            {walletRemoving ? '⏳ Removing...' : '✅ Confirm Remove'}
                          </button>
                          <button onClick={() => { setWalletConfirm(false); setWalletNote(''); }}
                            className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-500">
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">No BSC wallet registered. User can register one from their settings.</p>
                )}
              </div>

              {/* Email Change Section */}
              <h5 className="text-sm font-bold text-yellow-400 mt-4 mb-2">📧 User Email</h5>
              <div className="bg-gray-700/50 rounded-lg p-3 mb-3 border border-yellow-600/30">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className="text-gray-400 text-xs">Current Email:</span>
                    <span className="text-white text-sm ml-2 font-mono">{userEmail || '—'}</span>
                  </div>
                </div>
                {!emailConfirm ? (
                  <button onClick={() => setEmailConfirm(true)}
                    className="px-3 py-1.5 bg-yellow-700 text-white text-sm rounded hover:bg-yellow-600 transition-colors">
                    ✏️ Change Email
                  </button>
                ) : (
                  <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-3">
                    <p className="text-yellow-300 text-xs font-bold mb-2">⚠️ CONFIRM EMAIL CHANGE</p>
                    <p className="text-gray-300 text-xs mb-2">
                      This will change the user's login email address. They will need to use the new email to sign in.
                    </p>
                    <label className="text-xs text-gray-400 block mb-1">New Email Address *</label>
                    <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
                      placeholder="new@example.com"
                      className="w-full bg-gray-700 text-white text-sm px-3 py-1.5 rounded border border-yellow-600/50 focus:border-yellow-400 focus:outline-none mb-2" />
                    <label className="text-xs text-gray-400 block mb-1">Reason for change (required, min 10 chars) *</label>
                    <textarea value={emailReason} onChange={e => setEmailReason(e.target.value)}
                      placeholder="e.g. Client requested email change due to lost access to previous email..."
                      className="w-full bg-gray-700 text-white text-sm px-3 py-1.5 rounded border border-yellow-600/50 focus:border-yellow-400 focus:outline-none mb-2" rows={2} />
                    <div className="flex gap-2">
                      <button onClick={async () => {
                          setEmailChanging(true);
                          try {
                            const result = await callAPI('change_email', { newEmail, reason: emailReason });
                            if (result.success) {
                              setSuccess('✅ Email changed successfully');
                              setUserEmail(newEmail);
                              setNewEmail('');
                              setEmailReason('');
                              setEmailConfirm(false);
                            } else {
                              setError(result.error || 'Failed to change email');
                            }
                          } catch (err: any) {
                            setError(err.message);
                          } finally {
                            setEmailChanging(false);
                          }
                        }} disabled={emailChanging || !newEmail.trim() || emailReason.trim().length < 10}
                        className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-500 disabled:bg-gray-600 disabled:cursor-not-allowed">
                        {emailChanging ? '⏳ Changing...' : '✅ Confirm Change'}
                      </button>
                      <button onClick={() => { setEmailConfirm(false); setNewEmail(''); setEmailReason(''); }}
                        className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded hover:bg-gray-500">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* KYC Section */}
              <h5 className="text-sm font-bold text-purple-400 mt-4 mb-2">
                🔐 KYC Information {!hasKyc && <span className="text-red-400">(No KYC record — will CREATE)</span>}
              </h5>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <Field label="First Name *" value={kycData.first_name} onChange={(v: string) => setKycData({...kycData, first_name: v})} />
                <Field label="Last Name *" value={kycData.last_name} onChange={(v: string) => setKycData({...kycData, last_name: v})} />
                <Field label="Full Legal Name" value={kycData.full_legal_name} onChange={(v: string) => setKycData({...kycData, full_legal_name: v})} />
                <Field label="ID Number *" value={kycData.id_number_decrypted} onChange={(v: string) => setKycData({...kycData, id_number_decrypted: v})} placeholder={hasKyc ? '(leave blank to keep current)' : 'Required for new KYC'} />
                <Field label="ID Type" value={kycData.id_type} onChange={(v: string) => setKycData({...kycData, id_type: v})} placeholder="national_id / passport" />
                <Field label="Phone Number" value={kycData.phone_number} onChange={(v: string) => setKycData({...kycData, phone_number: v})} />
                <Field label="Email Address" value={kycData.email_address} onChange={(v: string) => setKycData({...kycData, email_address: v})} />
                <KycBirthdateField
                  text={dobText}
                  error={dobError}
                  onTextChange={(value) => {
                    setDobText(value);
                    setDobError(null);
                  }}
                  onCommit={(iso) => {
                    setDobError(null);
                    setKycData(prev => ({ ...prev, date_of_birth: iso }));
                    setDobText(isoDateToDayMonthYear(iso));
                  }}
                  onInvalid={() => setDobError('Use day/month/year, for example 12/10/1992')}
                />
                <Field label="Street Address" value={kycData.street_address} onChange={(v: string) => setKycData({...kycData, street_address: v})} />
                <Field label="City" value={kycData.city} onChange={(v: string) => setKycData({...kycData, city: v})} />
                <Field label="Province/State" value={kycData.province} onChange={(v: string) => setKycData({...kycData, province: v})} />
                <Field label="Postal Code" value={kycData.postal_code} onChange={(v: string) => setKycData({...kycData, postal_code: v})} />
                <Field label="Country Code" value={kycData.country_code} onChange={(v: string) => setKycData({...kycData, country_code: v})} placeholder="e.g. ZAF" />
                <Field label="Country Name" value={kycData.country_name} onChange={(v: string) => setKycData({...kycData, country_name: v})} />
                <Field label="Nationality" value={kycData.nationality} onChange={(v: string) => setKycData({...kycData, nationality: v})} />
              </div>
              {/* Document Verification Override */}
              <div className="mb-3 p-3 rounded-lg border border-yellow-600/40 bg-yellow-900/20">
                <div className="flex items-center gap-3">
                  <input type="checkbox" checked={kycData.document_verification_override || false}
                    onChange={e => setKycData({...kycData, document_verification_override: e.target.checked})}
                    className="w-4 h-4 accent-yellow-500" />
                  <div>
                    <label className="text-xs font-bold text-yellow-400">🔓 Document Verification Override</label>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Allow withdrawals without uploaded/approved KYC documents.
                      Use for manually verified users or legacy accounts.
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <label className="text-xs text-gray-400">Admin Notes</label>
                <textarea value={kycData.admin_notes || ''} onChange={e => setKycData({...kycData, admin_notes: e.target.value})}
                  className="w-full bg-gray-700 text-white text-sm px-3 py-1.5 rounded border border-gray-600 focus:border-purple-500 focus:outline-none" rows={2} />
              </div>
              <button onClick={saveKyc} disabled={saving}
                className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded hover:bg-purple-500 disabled:bg-gray-600">
                {saving ? 'Saving...' : hasKyc ? '💾 Save KYC Changes' : '➕ Create KYC Record'}
              </button>

              {/* KYC Documents Section */}
              <h5 className="text-sm font-bold text-green-400 mt-6 mb-2">📄 KYC Documents</h5>
              {!hasKyc && (
                <p className="text-yellow-400 text-xs mb-2">⚠️ Create KYC record first before uploading documents.</p>
              )}
              <div className="grid grid-cols-2 gap-3 mb-3">
                {DOC_TYPES.map(({ key, label, icon }) => {
                  const existingDoc = documents.find(d => d.document_type === key);
                  const isUploading = uploading === key;
                  return (
                    <div key={key} className="bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-lg">{icon}</span>
                        <span className="text-xs font-semibold text-white">{label}</span>
                      </div>
                      {existingDoc ? (
                        <div className="mb-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            existingDoc.verification_status === 'approved' ? 'bg-green-600 text-white' :
                            existingDoc.verification_status === 'rejected' ? 'bg-red-600 text-white' :
                            'bg-yellow-600 text-white'
                          }`}>
                            {existingDoc.verification_status}
                          </span>
                          <a href={existingDoc.url} target="_blank" rel="noopener noreferrer"
                            className="ml-2 text-xs text-blue-400 hover:text-blue-300 underline">
                            View ↗
                          </a>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(existingDoc.uploaded_at).toLocaleDateString()}
                            {existingDoc.file_size ? ` • ${(existingDoc.file_size / 1024).toFixed(0)}KB` : ''}
                          </p>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 mb-2">No document uploaded</p>
                      )}
                      <label className={`block w-full text-center text-xs py-1.5 rounded cursor-pointer transition-colors ${
                        isUploading || !hasKyc
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                          : existingDoc
                            ? 'bg-orange-600 hover:bg-orange-500 text-white'
                            : 'bg-green-600 hover:bg-green-500 text-white'
                      }`}>
                        {isUploading ? '⏳ Uploading...' : existingDoc ? '🔄 Replace' : '📤 Upload'}
                        <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp,application/pdf"
                          disabled={isUploading || !hasKyc}
                          onChange={e => { if (e.target.files?.[0]) handleFileUpload(key, e.target.files[0]); e.target.value = ''; }} />
                      </label>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminUserEditor;
